import { motion } from "framer-motion";
import { Check } from "lucide-react";
import SectionLabel from "./SectionLabel";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const AUDIENCE = ["Entrepreneurs", "Business Owners", "Working Professionals", "Consultants", "Freelancers", "Creators"];

const CONNECT_POINTS = [
  "Share and receive leads & referrals",
  "Explore collaborations within and across industries",
  "Exchange hiring & work opportunities",
  "Share your work, expertise & professional requirements",
  "Seek guidance, feedback & perspectives",
  "Connect through online & offline meetups",
  "Expand your social circle",
];

const P = "text-base md:text-lg text-black/70 leading-relaxed";

/** "transaction" struck through in red with "relationship" (green) sitting over it. Static. */
function RelationshipFirst() {
  return (
    <span className="relative inline-block align-baseline pt-[1.15em]" data-testid="relationship-first">
      <span
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 top-0 whitespace-nowrap font-display font-semibold text-[1em] leading-none text-emerald-600"
      >
        relationship
      </span>
      <span className="relative inline-block">
        <span className="sr-only">relationship</span>
        <span aria-hidden="true">transaction</span>
        <span
          aria-hidden="true"
          className="absolute left-0 right-0 top-[57%] h-[1.5px] -translate-y-1/2 bg-red-500 rounded-full pointer-events-none"
        />
      </span>
    </span>
  );
}

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="section-pad bg-white text-black"
    >
      <div className="container-x">
        <div>
          {/* 1. What is High Five Access? */}
          <motion.div {...reveal}>
            <SectionLabel dataTestId="about-overline">What is High Five Access?</SectionLabel>
            <div className="mt-7 md:mt-8 space-y-5">
              <p className={P} data-testid="about-p1">
                A lot of capable, growth-oriented people already exist around us. Yet
                many of them don&rsquo;t always get the opportunity to meet the right
                people. That&rsquo;s why High Five Access exists.
              </p>
              <p className={P} data-testid="about-p2">
                High Five Access is a <RelationshipFirst />-first curated professional
                community where working professionals, entrepreneurs, freelancers, creators, and
                business owners connect through city-wise WhatsApp communities,
                industry-specific circles, and curated online and offline networking
                meetups.
              </p>
              <p className={P} data-testid="about-p3">
                It is designed to help people build meaningful professional
                relationships, discover opportunities, collaborate across industries, and
                grow alongside the right community.
              </p>
            </div>
          </motion.div>

          <div className="hairline my-12 md:my-14" />

          {/* 2. Who Is It For? */}
          <motion.div {...reveal} id="who" data-testid="who-section" className="scroll-mt-28">
            <SectionLabel dataTestId="who-overline">Who Is It For?</SectionLabel>
            <p className={`${P} mt-7 md:mt-8`} data-testid="who-supporting">
              For people who value relationships over transactions, care about their
              work, and want to be around the right people.
            </p>
            <div
              className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 font-display font-semibold text-lg md:text-xl tracking-tight text-black"
              data-testid="who-audience"
            >
              {AUDIENCE.map((a, i) => (
                <span key={a} className="inline-flex items-center gap-x-3">
                  <span aria-hidden="true" className="text-orange-500">·</span>
                  <span>{a}</span>
                </span>
              ))}
            </div>
          </motion.div>

          <div className="hairline my-12 md:my-14" />

          {/* 3. How Members Connect */}
          <motion.div {...reveal} id="how" data-testid="value-section" className="scroll-mt-28">
            <SectionLabel dataTestId="how-overline">How Members Connect</SectionLabel>
            <div className="mt-7 md:mt-8 space-y-5">
              <p className={P} data-testid="how-p1">
                High Five Access is built around meaningful professional relationships.
              </p>
              <p className={P} data-testid="how-p1b">
                The more actively you participate, contribute, engage with other members,
                and attend online and offline meetups, the more familiarity, visibility,
                and trust you build within the network.
              </p>
              <p className={P} data-testid="how-p2">
                Over time, these relationships open up opportunities for leads,
                referrals, collaborations, hiring and work, while helping you find the
                right people for your own professional requirements.
              </p>
            </div>
            <ul
              className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-3.5"
              data-testid="how-points"
            >
              {CONNECT_POINTS.map((pt, i) => (
                <li key={pt} className="flex items-start gap-3 text-[15px] md:text-base text-black/80 leading-relaxed" data-testid={`how-point-${i + 1}`}>
                  <span className="mt-[5px] inline-flex w-5 h-5 rounded-full bg-orange-50 items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-orange-600" strokeWidth={3} />
                  </span>
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
